import CryptoJS from 'crypto-js';

/**
 * Encryption Utilities
 * Handles client-side encryption for sensitive data
 */

class Encryption {
  static SECRET_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'default-secret-key-change-me';

  /**
   * Encrypt data using AES
   */
  static encrypt(data) {
    try {
      if (!data) return null;
      
      const jsonString = typeof data === 'string' ? data : JSON.stringify(data);
      const encrypted = CryptoJS.AES.encrypt(jsonString, this.SECRET_KEY).toString();
      
      return encrypted;
    } catch (error) {
      console.error('Encryption error:', error);
      return null;
    }
  }

  /**
   * Decrypt AES encrypted data
   */
  static decrypt(encryptedData) {
    try {
      if (!encryptedData) return null;
      
      const bytes = CryptoJS.AES.decrypt(encryptedData, this.SECRET_KEY);
      const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
      
      if (!decryptedString) return null;
      
      try {
        return JSON.parse(decryptedString);
      } catch {
        return decryptedString;
      }
    } catch (error) {
      console.error('Decryption error:', error);
      return null;
    }
  }

  /**
   * Hash data using SHA256
   */
  static hash(data) {
    try {
      if (!data) return null;
      
      const dataString = typeof data === 'string' ? data : JSON.stringify(data);
      return CryptoJS.SHA256(dataString).toString();
    } catch (error) {
      console.error('Hashing error:', error);
      return null;
    }
  }

  /**
   * Generate random token
   */
  static generateToken(length = 32) {
    const randomWords = CryptoJS.lib.WordArray.random(length);
    return randomWords.toString(CryptoJS.enc.Hex);
  }

  /**
   * Encrypt object for localStorage
   */
  static encryptForStorage(key, data) {
    try {
      const encrypted = this.encrypt(data);
      if (encrypted) {
        localStorage.setItem(key, encrypted);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Storage encryption error:', error);
      return false;
    }
  }

  /**
   * Decrypt object from localStorage
   */
  static decryptFromStorage(key) {
    try {
      const encrypted = localStorage.getItem(key);
      if (!encrypted) return null;
      
      return this.decrypt(encrypted);
    } catch (error) {
      console.error('Storage decryption error:', error);
      return null;
    }
  }

  /**
   * Securely remove from localStorage
   */
  static removeFromStorage(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Storage removal error:', error);
      return false;
    }
  }

  /**
   * Generate CSRF token
   */
  static generateCSRFToken() {
    return this.generateToken(64);
  }

  /**
   * Base64 encode (URL safe)
   */
  static base64Encode(data) {
    try {
      const dataString = typeof data === 'string' ? data : JSON.stringify(data);
      const wordArray = CryptoJS.enc.Utf8.parse(dataString);
      return CryptoJS.enc.Base64.stringify(wordArray)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
    } catch (error) {
      console.error('Base64 encode error:', error);
      return null;
    }
  }

  /**
   * Base64 decode (URL safe)
   */
  static base64Decode(encoded) {
    try {
      let base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
      
      // Add padding
      while (base64.length % 4) {
        base64 += '=';
      }
      
      const wordArray = CryptoJS.enc.Base64.parse(base64);
      const decoded = wordArray.toString(CryptoJS.enc.Utf8);
      
      try {
        return JSON.parse(decoded);
      } catch {
        return decoded;
      }
    } catch (error) {
      console.error('Base64 decode error:', error);
      return null;
    }
  }

  /**
   * Create HMAC signature
   */
  static createHMAC(data, secret = this.SECRET_KEY) {
    try {
      const dataString = typeof data === 'string' ? data : JSON.stringify(data);
      return CryptoJS.HmacSHA256(dataString, secret).toString();
    } catch (error) {
      console.error('HMAC creation error:', error);
      return null;
    }
  }

  /**
   * Verify HMAC signature
   */
  static verifyHMAC(data, signature, secret = this.SECRET_KEY) {
    try {
      const computed = this.createHMAC(data, secret);
      return computed === signature;
    } catch (error) {
      console.error('HMAC verification error:', error);
      return false;
    }
  }
}

export default Encryption;
